import type { AlgorithmOperation } from "@algoquest/schemas";
import { BinarySearchEngine, type BinarySearchDomainState } from "./binary-search";
import type { SearchActionOutcome, SearchController, SearchViewState } from "./SearchController";

function toView(state: BinarySearchDomainState): SearchViewState {
  return {
    mode: "binary-search",
    values: state.values,
    target: state.target,
    focusIndex: state.mid,
    awaitingChoice: state.awaitingChoice,
    range: [state.low, state.high],
    checkedIndices: state.checkedIndices,
    eliminatedRanges: state.eliminatedRanges,
    foundIndex: state.foundIndex,
    comparisons: state.comparisons,
    mistakes: state.mistakes,
    isComplete: state.isComplete,
  };
}

/**
 * Wraps BinarySearchEngine behind the shared SearchController contract
 * (Phase 04). `handlePrimaryAction` is the "inspect the mid box" action;
 * `handleDirectionChoice` is the "commit to a half" action. The engine
 * itself decides whether a direction is correct — this controller only
 * translates its result into the shared outcome vocabulary.
 */
export class BinarySearchController implements SearchController {
  readonly mode = "binary-search" as const;
  private readonly engine: BinarySearchEngine;

  constructor(values: number[], target: number) {
    this.engine = new BinarySearchEngine(values, target);
  }

  getView(): SearchViewState {
    return toView(this.engine.getState());
  }

  getTrace(): AlgorithmOperation[] {
    return this.engine.getTrace();
  }

  handlePrimaryAction(nearestIndex: number | null): SearchActionOutcome {
    const state = this.engine.getState();
    if (state.isComplete || state.awaitingChoice || state.mid === null || nearestIndex === null) {
      return { kind: "ignored" };
    }
    if (state.mid !== nearestIndex) {
      return { kind: "rejected-wrong-box", expectedIndex: state.mid };
    }
    const result = this.engine.inspect(performance.now());
    if (!result) {
      return { kind: "ignored" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }

  handleDirectionChoice(direction: "left" | "right"): SearchActionOutcome {
    const state = this.engine.getState();
    if (state.isComplete || !state.awaitingChoice) {
      return { kind: "ignored" };
    }
    const { wasCorrect, result } = this.engine.chooseDirection(direction, performance.now());
    if (!wasCorrect || !result) {
      return { kind: "rejected-wrong-direction" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }
}
